import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { FaCheckCircle } from "react-icons/fa";
import SEO from "../../components/SEO";

// tiyatrokumpanyasi@gmail.com is already used as the public contact
// address elsewhere in the app (Datenschutz page, ticket confirmation mail).
const CONTACT_EMAIL = "tiyatrokumpanyasi@gmail.com";

type ContactStatus = "idle" | "submitting" | "success" | "error";

const Impressum: React.FC = () => {
  const { t } = useTranslation();
  const [contactStatus, setContactStatus] = useState<ContactStatus>("idle");

  const mailtoHref = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(
    t("impressum.contactSubject")
  )}`;

  const handleContactSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setContactStatus("submitting");

    const formData = new FormData(e.currentTarget);

    fetch("/api/contact_messages", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        name: formData.get("name"),
        email: formData.get("email"),
        message: formData.get("message"),
        website: formData.get("website"),
      }),
    })
      .then((res) => {
        if (!res.ok) {
          return res.json().then((err) => {
            console.error("Sunucu hatası:", err);
            throw new Error("Mesaj gönderilemedi");
          });
        }
        return res.json();
      })
      .then(() => setContactStatus("success"))
      .catch((err) => {
        console.error("İletişim formu hatası:", err);
        setContactStatus("error");
      });
  };

  return (
    <section className="w-full flex-1 flex flex-col items-center justify-center px-4 py-1 mt-8 bg-black text-white">
      <div className="w-full max-w-5xl">
        <SEO
          title={t("pageTitles.impressum")}
          description={t("impressum.seoDescription")}
          keywords={t("impressum.seoKeywords")}
        />

        <h1 className="text-2xl sm:text-3xl font-extrabold underline underline-offset-8 mb-12 decoration-brand-red-text text-center">
          {t("impressum.title")}
        </h1>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-x-12 gap-y-8">
          {/* Legal info column - must stay first in the DOM so it's on top on mobile too */}
          <div className="text-center xl:text-left">
            <h2 className="text-2xl font-bold mb-4 text-brand-red-text">
              {t("impressum.legalInfoTitle")}
            </h2>
            <div className="space-y-4 text-gray-300 leading-relaxed">
              <p className="text-white font-semibold">
                {t("impressum.organizationName")}
              </p>
              <p>{t("impressum.address")}</p>

              <p>
                <span className="text-gray-400">
                  {t("impressum.registerNumberLabel")}:{" "}
                </span>
                {t("impressum.registerNumber")}
              </p>
              <p>
                <span className="text-gray-400">
                  {t("impressum.registerCourtLabel")}:{" "}
                </span>
                {t("impressum.registerCourt")}
              </p>

              <div>
                <p className="text-gray-400">
                  {t("impressum.representativesLabel")}:
                </p>
                <p>
                  {t("impressum.presidentLabel")}: {t("impressum.presidentName")}
                </p>
                <p>
                  {t("impressum.vicePresidentLabel")}:{" "}
                  {t("impressum.vicePresidentName")}
                </p>
                <p>
                  {t("impressum.treasurerLabel")}: {t("impressum.treasurerName")}
                </p>
              </div>

              <p>
                <span className="text-gray-400">
                  {t("impressum.emailLabel")}:{" "}
                </span>
                {CONTACT_EMAIL}
              </p>

              {/* TODO: USt-IdNr needed - add only if the Verein is VAT-liable, per user confirmation */}
            </div>
          </div>

          {/* Contact column - real form, mailto kept as a fallback if it fails */}
          <div className="text-center xl:text-left">
            <h2 className="text-2xl font-bold mb-4 text-brand-red-text">
              {t("impressum.contactTitle")}
            </h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              {t("impressum.contactIntro")}
            </p>

            {contactStatus === "success" ? (
              <div>
                <p className="text-xl font-semibold text-white mb-2 flex items-center justify-center xl:justify-start gap-2">
                  <FaCheckCircle
                    className="text-brand-red-text text-2xl shrink-0"
                    aria-hidden="true"
                  />
                  <span>{t("impressum.contactForm.successTitle")}</span>
                </p>
                <p className="text-gray-300">
                  {t("impressum.contactForm.successMessage")}
                </p>
              </div>
            ) : (
              <form
                onSubmit={handleContactSubmit}
                className="flex flex-col gap-4 max-w-md mx-auto xl:mx-0"
              >
                {/* Honeypot: hidden from sighted/keyboard/AT users, left for bots that fill every field */}
                <input
                  type="text"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  className="absolute -left-[9999px] w-px h-px overflow-hidden"
                />

                <div>
                  <label
                    htmlFor="contact-name"
                    className="block text-sm font-semibold text-gray-200 mb-1"
                  >
                    {t("impressum.contactForm.nameLabel")}
                  </label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    className="w-full bg-zinc-800 border border-zinc-600 text-white rounded px-4 py-2 placeholder-gray-400"
                    placeholder={t("impressum.contactForm.namePlaceholder")}
                  />
                </div>

                <div>
                  <label
                    htmlFor="contact-email"
                    className="block text-sm font-semibold text-gray-200 mb-1"
                  >
                    {t("impressum.contactForm.emailLabel")}
                  </label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    className="w-full bg-zinc-800 border border-zinc-600 text-white rounded px-4 py-2 placeholder-gray-400"
                    placeholder={t("impressum.contactForm.emailPlaceholder")}
                  />
                </div>

                <div>
                  <label
                    htmlFor="contact-message"
                    className="block text-sm font-semibold text-gray-200 mb-1"
                  >
                    {t("impressum.contactForm.messageLabel")}
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    rows={4}
                    className="w-full bg-zinc-800 border border-zinc-600 text-white rounded px-4 py-2 placeholder-gray-400"
                    placeholder={t("impressum.contactForm.messagePlaceholder")}
                  />
                </div>

                <button
                  type="submit"
                  disabled={contactStatus === "submitting"}
                  className="bg-[#D8001D] hover:bg-[#B8001A] text-white py-2 rounded font-semibold w-full cursor-pointer disabled:opacity-60"
                >
                  {contactStatus === "submitting"
                    ? t("impressum.contactForm.submitting")
                    : t("impressum.contactForm.submit")}
                </button>

                {contactStatus === "error" && (
                  <div>
                    <p className="text-red-400 text-sm mb-1">
                      {t("impressum.contactForm.error")}
                    </p>
                    <p className="text-gray-400 text-sm">
                      {t("impressum.contactForm.errorFallback")}{" "}
                      <a
                        href={mailtoHref}
                        className="text-brand-red-text hover:underline"
                      >
                        {CONTACT_EMAIL}
                      </a>
                    </p>
                  </div>
                )}
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Impressum;
